import { useState, useEffect } from "react";
import { colors, fonts, frames, glyphs } from "../../../../../Styles";
import { AicoConnection, NewSelect, PageHeader, useAicoConnection } from "../../../../../Components";
import { ButtonTypes, NewFieldStyles } from "../../../../../Definitions";
import NewButton from "../../../../../Components/BaseComponents/newButton";
import { useLazyQuery, useMutation } from "@apollo/client";
import { AVAILABLE_FIRMWARE_LC, AvailableFirmwareLCArgs, AvailableFirmwareLCResponse, FIRMWARE_UPDATE_ABORT, FIRMWARESYSTEM_INFO, FirmwareUpdateAbortArgs, FirmwareUpdateAbortResponse, LCFirmwareArgs, LCFirmwareResponse, UPDATEFIRMWARE_SYSTEM, UpdateFirmwareArgs, UpdateFirmwareResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/firmwareCalls";
import { LoadingSpinner } from "../../../../../Services/wrapper";


export default function Firmware() {
	const [firmwareFileName, setFirmwareFileName] = useState<string | null>(null);

	const { state: connectionState } = useAicoConnection();

	const [fetchAvailable, { data: dataAvailable, loading: loadingAvailable, error: errorAvailable }] = useLazyQuery<AvailableFirmwareLCResponse, AvailableFirmwareLCArgs>(
		AVAILABLE_FIRMWARE_LC,
		{ fetchPolicy: "network-only" }
	);

	const [fetchCurrent, { data: dataCurrent, loading: loadingCurrent, error: errorCurrent }] = useLazyQuery<LCFirmwareResponse, LCFirmwareArgs>(
		FIRMWARESYSTEM_INFO,
		{ fetchPolicy: "network-only" }
	);

	const [updateFirmware, { data: dataUpdate, loading: loadingUpdate, error: errorUpdate }] = useMutation<UpdateFirmwareResponse, UpdateFirmwareArgs>(
		UPDATEFIRMWARE_SYSTEM,
		{ fetchPolicy: "network-only" }
	);

	const [firmwareUpdateAbort, { data: dataAbort, loading: loadingAbort, error: errorAbort }] = useMutation<FirmwareUpdateAbortResponse, FirmwareUpdateAbortArgs>(
		FIRMWARE_UPDATE_ABORT,
		{ fetchPolicy: "network-only" }
	);

	useEffect(() => {
		if (connectionState.domain) {
			fetchAvailable({ variables: { domain: connectionState.domain } });
		} else {
			setFirmwareFileName(null);
		}

		if (connectionState.deviceConnection) {
			fetchCurrent({ variables: { ...connectionState.deviceConnection } });
		}
	}, [connectionState]);

	const selectFirmwareFile = firmwareFileName ? { label: firmwareFileName, value: firmwareFileName, id: firmwareFileName } : undefined;

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Aico Software' subHeader='Update LC Firmware' />
			<frames._page_inner_content>
				<frames._grid_topBtns_outer>

					<frames._grid_topBtns_inner style={{ position: "relative", justifyContent: "end", gap: "5px" }}>
						<div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: "20px" }}><AicoConnection/></div>
						<NewButton
							content={loadingAvailable ? "Fetching..." : "Fetch avail."}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.domain) {
									fetchAvailable({ variables: { domain: connectionState.domain } });
								}
							}}
							disabled={loadingAvailable || !connectionState.deviceConnection}
						/>
						<NewButton
							content={loadingCurrent ? "Fetching..." : "Fetch curr."}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.deviceConnection) {
									fetchCurrent({ variables: { ...connectionState.deviceConnection } });
								}
							}}
							disabled={loadingCurrent || !connectionState.deviceConnection}
						/>
					</frames._grid_topBtns_inner>

					<div style={{ gridArea: "2/1/3/2", border: "5px solid " + colors.greyPrimary, overflow: "hidden", borderRadius: "10px", backgroundColor: colors.greyLightTertiary, display: "flex", flexDirection: "column", gap: "40px", alignItems: "center", justifyContent: "flex-start", padding: "20px" }}>

						<div style={{ display: "flex", gap: "10px", alignItems: "flex-end", justifyContent: "center", flexWrap: "wrap" }}>
							<NewSelect
								header="Update Firmware"
								placeholder="New firmware version"
								onChange={(selectedOption) => {
									if (selectedOption?.value) {
										setFirmwareFileName(selectedOption?.value)
									}
								}}
								options={dataAvailable?.controller.availableFirmware.map((firmwareVersion) => ({ label: firmwareVersion.fileName, value: firmwareVersion.fileName })) ?? []}
								fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
								isRequired={true}
								isSearchable={true}
								valueOverrides={{defaultValue: selectFirmwareFile}}
							/>
							<NewButton
								onClick={() => {
									if (firmwareFileName && connectionState.deviceConnection) {
										updateFirmware({ variables: { ...connectionState.deviceConnection, fileName: firmwareFileName } })
									}
								}}
								disabled={loadingUpdate || !firmwareFileName || !connectionState.deviceConnection}
								content={loadingUpdate ? "Updating..." : "Update Firmware"}
								overrides={{ baseStyles: { width: "fit-content", height: "30px", padding: "0 10px" } }}
							/>
							<NewButton
								onClick={() => {
									if (connectionState.deviceConnection) {
										firmwareUpdateAbort({ variables: { ...connectionState.deviceConnection } })
									}
								}}
								disabled={loadingAbort || !connectionState.deviceConnection}
								content={"ABORT"}
								overrides={{
									baseStyles: {
										width: "60px",
										height: "60px",
										borderRadius: "50%",
										backgroundColor: "red",
										color: "white",
										fontWeight: "bold",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										boxShadow: "0 0 10px rgba(255, 0, 0, 0.5)",
										cursor: "pointer",
										transition: "transform 0.2s, box-shadow 0.2s",
									},
									hoverStyles: {
										backgroundColor: "red",
										transform: "scale(1.1)",
										boxShadow: "0 0 15px rgba(255, 0, 0, 0.7)",
									},
								}}
							/>
						</div>

						<div style={{ width: "100%", maxWidth: "500px" }}>
							<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>
								Available Firmware Versions
							</div>
							{loadingAvailable && <LoadingSpinner />}
							{errorAvailable && <div style={{ color: "#aa0000" }}>Error</div>}
							{!loadingAvailable && !errorAvailable ? dataAvailable?.controller.availableFirmware.length
								?
								dataAvailable.controller.availableFirmware.map((firmwareVersion, i) => (
									<div key={i} style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left", ...fonts.fontSize3, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
										{firmwareVersion.fileName}
									</div>
								))
								:
								<div style={{ textAlign: "left", fontStyle: "italic", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyTertiary }}>
									No firmware available
								</div>
								:
								<></>
							}
						</div>

						<div style={{ width: "100%", maxWidth: "500px" }}>
							<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>
								Current firmware
							</div>
							{loadingCurrent && <LoadingSpinner />}
							{errorCurrent && <div style={{ color: "#aa0000" }}>Error</div>}
							{!loadingCurrent && !errorCurrent ? dataCurrent?.controller.config
								?
								<div style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left", ...fonts.fontSize3, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
									{dataCurrent.controller.config.firmwareVersion}
								</div>
								:
								<div style={{ textAlign: "left", fontStyle: "italic", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyTertiary }}>
									Unknown
								</div>
								:
								<></>
							}
						</div>

						{loadingAbort && <LoadingSpinner />}
						{(errorAbort || (dataAbort && !dataAbort.controller.firmwareUpdateAbort.success))
							&& <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px", textAlign: "center" }}>Error: failed to abort firmware update</p>
						}
						{!loadingAbort
							&& !errorAbort
							&& dataAbort?.controller.firmwareUpdateAbort.success
							&& <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemGreen, maxWidth: "500px", textAlign: "center" }}>Succesfully aborted firmware update</p>
						}

						{loadingUpdate && <LoadingSpinner />}
						{(errorUpdate || (dataUpdate && !dataUpdate.controller.firmwareUpdate.success))
							&& <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px", textAlign: "center" }}>Error: failed to update firmware</p>
						}
						{!loadingUpdate
							&& !errorUpdate
							&& dataUpdate?.controller.firmwareUpdate.success
							&& <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemGreen, maxWidth: "500px", textAlign: "center" }}>Succesfully updated firmware</p>
						}
					</div>
				</frames._grid_topBtns_outer>
			</frames._page_inner_content>
		</frames.Page_inner>
	);
}